import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  Collapse,
  Typography
} from '@mui/material';
import axios from 'axios';
import { colors } from '../../themes/typography';
import { API_BASE_URL, createAuthHeader, fetchLatestPrice } from '../../config/api.config';

/**
 * Self-contained inline product selection component for experiment cards
 * Fetches its own products based on clientId
 */
const ProductSelectionInline = ({
  show,
  onApply,
  onCancel,
  t,
  clientId
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reason, setReason] = useState('');
  const [price, setPrice] = useState('');
  const [errors, setErrors] = useState({});
  
  // Self-contained product state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  
  // Load products when component is shown and has clientId
  useEffect(() => {
    if (show && clientId && products.length === 0) {
      loadProducts();
    }
  }, [show, clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const loadProducts = async () => {
    setLoading(true);
    setLoadError(null);
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_BASE_URL}/all-products?clientId=${clientId}`,
        { headers: createAuthHeader(token) }
      );
      
      const productList = response.data.products || [];
      setProducts(productList);
    } catch (error) {
      console.error('[ProductSelectionInline] Error loading products:', error);
      setLoadError(t('errorLoadingProducts') || 'Error loading products');
    } finally {
      setLoading(false);
    }
  };

  // Reset state when component is shown/hidden
  useEffect(() => {
    if (!show) {
      setSelectedProduct(null);
      setReason('');
      setPrice('');
      setErrors({});
      setLoadError(null);
    }
  }, [show]);

  const handleApply = () => {
    const newErrors = {};
    
    if (!selectedProduct) {
      newErrors.product = t('pleaseSelectProduct') || 'Please select a product';
    }
    
    if (!reason.trim()) {
      newErrors.reason = t('pleaseProvideReason') || 'Please provide a reason';
    }
    
    if (!price.trim()) {
      newErrors.price = t('pleaseProvidePrice') || 'Please provide a price';
    } else {
      // Convert comma decimal to dot decimal and validate
      const normalizedPrice = price.replace(',', '.');
      const numericPrice = parseFloat(normalizedPrice);
      if (isNaN(numericPrice) || numericPrice <= 0) {
        newErrors.price = t('pleaseProvideValidPrice') || 'Please provide a valid price';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Convert price for API call (comma to dot)
    const normalizedPrice = price.replace(',', '.');
    onApply(selectedProduct.id, selectedProduct.name, reason.trim(), normalizedPrice);
  };

  return (
    <Collapse in={show}>
      <Box sx={{
        p: '6px',
        mt: '8px',
        borderRadius: '4px',
        border: `1px solid ${colors.border.light}`,
        backgroundColor: 'transparent'
      }}>
        {/* Micro-label for context */}
        <Box sx={{ mb: '4px' }}>
          <Typography sx={{
            fontSize: '9px',
            color: colors.text.secondary,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            lineHeight: 1
          }}>
            {t('selectNewProduct') || 'Select New Product'}
          </Typography>
        </Box>

        {/* Product Selection */}
        <Autocomplete
          value={selectedProduct}
          onChange={async (_, newValue) => {
            setSelectedProduct(newValue);
            if (newValue) {
              setErrors(prev => ({ ...prev, product: undefined }));
              
              // Fetch and populate latest price if clientId is available
              if (clientId) {
                try {
                  const priceData = await fetchLatestPrice(newValue.id, clientId);
                  if (priceData.price !== null) {
                    // Convert dot decimal to comma decimal for display
                    const formattedPrice = priceData.price.toString().replace('.', ',');
                    setPrice(formattedPrice);
                    setErrors(prev => ({ ...prev, price: undefined }));
                  }
                } catch (error) {
                  // Don't show error to user, just continue without pre-populating
                }
              }
            }
          }}
          options={products}
          getOptionLabel={(option) => option.name || ''}
          loading={loading}
          disabled={!!loadError}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={loadError || (t('searchProducts') || 'Search products...')}
              error={!!errors.product || !!loadError}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={12} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                sx: {
                  fontSize: '10px',
                  '& input': {
                    fontSize: '10px',
                    padding: '4px 6px',
                    color: loadError ? colors.text.secondary : colors.text.primary
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: loadError ? '#dc2626' : colors.border.medium,
                    borderRadius: '4px'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: loadError ? '#dc2626' : colors.border.dark
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: loadError ? '#dc2626' : colors.action.primary,
                    borderWidth: '1px'
                  }
                }
              }}
            />
          )}
          PaperProps={{
            sx: {
              '& .MuiAutocomplete-option': {
                fontSize: '10px',
                padding: '4px 8px',
                minHeight: 'auto'
              }
            }
          }}
          sx={{ mb: '4px' }}
        />

        {/* Error message for product */}
        {errors.product && (
          <Typography sx={{
            fontSize: '9px',
            color: '#dc2626',
            mb: '4px',
            fontWeight: 400
          }}>
            {errors.product}
          </Typography>
        )}

        {/* Reason Input */}
        <TextField
          placeholder={t('reasonForChange') || 'Reason for change...'}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (e.target.value.trim()) {
              setErrors(prev => ({ ...prev, reason: undefined }));
            }
          }}
          fullWidth
          error={!!errors.reason}
          InputProps={{
            sx: {
              fontSize: '10px',
              '& input': {
                fontSize: '10px',
                padding: '4px 6px',
                color: colors.text.primary
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.medium,
                borderRadius: '4px'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.dark
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.action.primary,
                borderWidth: '1px'
              }
            }
          }}
          sx={{ mb: '4px' }}
        />

        {/* Error message for reason */}
        {errors.reason && (
          <Typography sx={{
            fontSize: '9px',
            color: '#dc2626',
            mb: '4px',
            fontWeight: 400
          }}>
            {errors.reason}
          </Typography>
        )}

        {/* Price Input */}
        <TextField
          placeholder={t('priceForProduct') || '1,50'}
          value={price}
          onChange={(e) => {
            setPrice(e.target.value);
            if (e.target.value.trim()) {
              setErrors(prev => ({ ...prev, price: undefined }));
            }
          }}
          fullWidth
          error={!!errors.price}
          InputProps={{
            sx: {
              fontSize: '10px',
              '& input': {
                fontSize: '10px',
                padding: '4px 6px',
                color: colors.text.primary
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.medium,
                borderRadius: '4px'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.dark
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.action.primary,
                borderWidth: '1px'
              }
            }
          }}
          sx={{ mb: '4px' }}
        />

        {/* Error message for price */}
        {errors.price && (
          <Typography sx={{
            fontSize: '9px',
            color: '#dc2626',
            mb: '4px',
            fontWeight: 400
          }}>
            {errors.price}
          </Typography>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
          <Button
            onClick={onCancel}
            sx={{
              fontSize: '9px',
              textTransform: 'none',
              color: '#dc2626',
              backgroundColor: 'rgba(220, 38, 38, 0.08)',
              border: '1px solid rgba(220, 38, 38, 0.2)',
              padding: '2px 6px',
              minWidth: 'auto',
              minHeight: 'auto',
              borderRadius: '4px',
              fontWeight: 600,
              boxShadow: '0 1px 2px rgba(220, 38, 38, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(220, 38, 38, 0.12)',
                borderColor: 'rgba(220, 38, 38, 0.3)',
                color: '#dc2626',
                boxShadow: '0 2px 4px rgba(220, 38, 38, 0.15)'
              }
            }}
          >
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleApply}
            sx={{
              fontSize: '9px',
              textTransform: 'none',
              backgroundColor: '#059669',
              color: 'white',
              border: '1px solid #047857',
              padding: '3px 8px',
              minWidth: 'auto',
              minHeight: 'auto',
              borderRadius: '4px',
              fontWeight: 700,
              boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)',
              '&:hover': {
                backgroundColor: '#047857',
                borderColor: '#065f46',
                color: 'white',
                boxShadow: '0 3px 6px rgba(5, 150, 105, 0.3)',
                transform: 'translateY(-1px)'
              },
              '&:active': {
                transform: 'translateY(0px)',
                boxShadow: '0 1px 2px rgba(5, 150, 105, 0.2)'
              }
            }}
          >
            {t('applySelection') || 'Apply Selection'}
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
};

export default ProductSelectionInline;